/**
 * Role-based access control types for the hybrid application
 */

export type AppRole =
  | 'end_user'
  | 'company_admin'
  | 'internal_admin'
  | 'super_admin'
  | 'data_administrator'
  | 'viewer';

interface Roles {
  id: AppRole;
  name: string;
  description?: string;
}

export const ROLES: Record<AppRole, Roles> = {
  end_user: {
    id: 'end_user',
    name: 'End User',
    description: 'Regular end user with limited access',
  },
  company_admin: {
    id: 'company_admin',
    name: 'Company Admin',
    description: 'Administrator with access to company-specific data',
  },
  internal_admin: {
    id: 'internal_admin',
    name: 'Internal Admin',
    description: 'Administrator with elevated privileges across companies',
  },
  super_admin: {
    id: 'super_admin',
    name: 'Super Admin',
    description: 'Top-level administrator with full access',
  },
  data_administrator: {
    id: 'data_administrator',
    name: 'Data Administrator',
    description: 'Manages data contracts and data-related tasks for a company',
  },
  viewer: {
    id: 'viewer',
    name: 'Viewer',
    description: 'Read-only access to company data',
  },
} as const;

export interface UserRole {
  id: string;
  user_id: string;
  role: AppRole;
  assigned_by?: string;
  assigned_at: string;
  company_id?: string;
  division_id?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface RoleRoute {
  path: string;
  component: React.ComponentType;
  roles: AppRole[];
  fallback?: string;
}

export interface RolePermissions {
  canViewUsers: boolean;
  canManageUsers: boolean;
  canViewCompanies: boolean;
  canManageCompanies: boolean;
  canManageOwnCompany: boolean;
  canViewAnalytics: boolean;
  canManageSystem: boolean;
  canViewAuditLogs: boolean;
}

export const ROLE_HIERARCHY: Record<AppRole, number> = {
  super_admin: 1,
  internal_admin: 2,
  company_admin: 3,
  data_administrator: 4,
  viewer: 5,
  end_user: 6,
} as const;

export const ADMIN_MANAGEABLE_ROLES = [
  'super_admin',
  'internal_admin',
  'company_admin',
  'data_administrator',
  'viewer',
  'end_user',
  'public',
] as const satisfies readonly [...AppRole[], 'public'];

export const ROLE_PERMISSIONS: Record<AppRole, RolePermissions> = {
  super_admin: {
    canViewUsers: true,
    canManageUsers: true,
    canViewCompanies: true,
    canManageCompanies: true,
    canManageOwnCompany: true,
    canViewAnalytics: true,
    canManageSystem: true,
    canViewAuditLogs: true,
  },
  internal_admin: {
    canViewUsers: true,
    canManageUsers: true,
    canViewCompanies: true,
    canManageCompanies: true,
    canManageOwnCompany: true,
    canViewAnalytics: true,
    canManageSystem: true,
    canViewAuditLogs: true,
  },
  company_admin: {
    canViewUsers: true,
    canManageUsers: true,
    canViewCompanies: false,
    canManageCompanies: false,
    canManageOwnCompany: true,
    canViewAnalytics: true,
    canManageSystem: false,
    canViewAuditLogs: false,
  },
  data_administrator: {
    canViewUsers: true,
    canManageUsers: false,
    canViewCompanies: false,
    canManageCompanies: false,
    canManageOwnCompany: false,
    canViewAnalytics: true,
    canManageSystem: false,
    canViewAuditLogs: false,
  },
  viewer: {
    canViewUsers: false,
    canManageUsers: false,
    canViewCompanies: false,
    canManageCompanies: false,
    canManageOwnCompany: false,
    canViewAnalytics: true,
    canManageSystem: false,
    canViewAuditLogs: false,
  },
  end_user: {
    canViewUsers: false, // End users can only view their own data
    canManageUsers: false,
    canViewCompanies: false,
    canManageCompanies: false,
    canManageOwnCompany: false,
    canViewAnalytics: false,
    canManageSystem: false,
    canViewAuditLogs: false,
  },
} as const;
